<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';
import useBreakpoints from '@/composables/useBreakpoints';
import useNetwork from '@/composables/useNetwork';
import useNotifications from '@/composables/useNotifications';
import useWeb3 from '@/services/web3/useWeb3';
import { configService } from '@/services/config/config.service';
import { prodNetworkConfigs, devNetworkConfigs } from '@/lib/config';
import { Config } from '@/lib/config/types';
import { buildNetworkIconURL } from '@/lib/utils/urls';
import { hardRedirectTo } from '@/plugins/router/nav-guards';

export interface NetworkOption {
  id: string;
  name: string;
  networkSlug?: string;
  key?: string;
  isAvailable?: boolean;
}

// COMPOSABLES
const { upToLargeBreakpoint } = useBreakpoints();
const { networkId, networkConfig, getNetworkChangeUrl } = useNetwork();
const { chainId } = useWeb3();
const router = useRouter();
const { addNotification } = useNotifications();
const { t } = useI18n();

function convertConfigToNetworkOption(config: Config): NetworkOption {
  return {
    id: config.slug,
    name: config.chainName,
    networkSlug: config.slug,
    key: config.key,
    isAvailable: (() => {
      if (import.meta.env.MODE === 'production') {
        // return config.key === '137';
      }

      return true;
    })(),
  };
}

const prodNetworks: NetworkOption[] = prodNetworkConfigs
  .filter(config => config.visibleInUI && !config.testNetwork)
  .map(convertConfigToNetworkOption);

const networks = ref(prodNetworks);

const testNetworks: NetworkOption[] = devNetworkConfigs
  .filter(config => config.visibleInUI && config.testNetwork)
  .map(convertConfigToNetworkOption);

const networksDev = ref(testNetworks);

// COMPUTED
const allNetworks = computed(() => {
  return networks.value.concat(
    configService.env.APP_ENV === 'development' ||
      configService.env.APP_ENV === 'staging'
      ? networksDev.value
      : []
  );
});

const visibleNetworks = computed(() => {
  if (import.meta.env.MODE === 'production') {
    return prodNetworks;
  }

  return testNetworks;
});

const appNetworkSupported = computed((): boolean => {
  return allNetworks.value
    .map(network => network.key)
    .includes(networkId.value.toString());
});

const activeNetwork = computed((): NetworkOption | undefined => {
  return allNetworks.value.find(network => {
    if (!appNetworkSupported.value && network.id === 'ethereum') return true;
    return isActive(network);
  });
});

const isOnboardingPage = computed(() => {
  return router.currentRoute.value.name?.toString() === 'onboarding';
});

// LIFECYCLE
onMounted(async () => {
  await router.isReady();
  if (router.currentRoute.value.query?.poolNetworkAlert) {
    addNotification({
      type: 'error',
      title: '',
      message: `${t('poolDoesntExist')} ${networkConfig.chainName}`,
    });
    router.replace({ query: {} });
  }
});

// WATCHERS
watch(chainId, (newChainId, oldChainId) => {
  if (
    newChainId &&
    oldChainId &&
    oldChainId !== newChainId &&
    networkId.value !== newChainId
  ) {
    const newNetwork = allNetworks.value.find(
      n => Number(n.key) === newChainId
    );
    if (newNetwork) {
      localStorage.setItem('networkId', newChainId.toString());
      hardRedirectTo(getNetworkChangeUrl(newNetwork.networkSlug));
    }
  }
});

// METHODS
function isActive(network: NetworkOption): boolean {
  if (!appNetworkSupported.value && network.id === 'ethereum') return true;
  return networkId.value.toString() === network.key;
}
</script>

<template>
  <BalPopover noPad>
    <template #activator>
      <BalBtn color="white" :size="upToLargeBreakpoint ? 'md' : 'sm'">
        <template v-if="activeNetwork">
          <img
            :src="buildNetworkIconURL(activeNetwork.id)"
            :alt="activeNetwork.name"
            class="w-6 h-6 rounded-full"
          />
          <span class="ml-2">
            {{ activeNetwork.name }}
          </span>
          <BalIcon
            v-if="!isOnboardingPage"
            name="chevron-down"
            size="sm"
            class="ml-2"
          />
        </template>
      </BalBtn>
    </template>
    <div
      v-if="!isOnboardingPage"
      role="menu"
      class="flex overflow-hidden flex-col w-52 rounded-lg"
    >
      <div
        class="py-2 px-3 text-sm font-medium text-gray-500 whitespace-nowrap bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-900"
      >
        {{ $t('networkSelection') }}:
      </div>
      <a
        v-for="network in visibleNetworks"
        :key="network.id"
        :href="
          network.isAvailable
            ? getNetworkChangeUrl(network.networkSlug)
            : undefined
        "
        :class="[
          'flex justify-between items-center p-3',
          network.isAvailable
            ? 'hover:bg-gray-50 dark:hover:bg-gray-850 cursor-pointer'
            : 'mouse-event-none grayscale',
        ]"
      >
        <div class="flex items-center">
          <img
            :src="buildNetworkIconURL(network.id)"
            :alt="network.name"
            class="mr-2 w-6 h-6 rounded-full"
          />
          <span class="ml-1 font-medium">
            {{ network.name }}
          </span>
        </div>
        <BalIcon
          v-if="isActive(network)"
          name="check"
          class="text-blue-500 dark:text-blue-400"
        />
      </a>
    </div>
  </BalPopover>
</template>
